import { Component } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import classNames from 'classnames'
import withStyles from 'beam/dist/styles'
import Tooltip from 'beam/dist/Tooltip'

import { Rect, Text } from 'victory'
import { msToHHMMSS } from 'beam/dist/utils/time'

import { isEmpty } from 'lodash'
import { difference } from '../../../Common/Utils'

const styles = theme => ({
  svg: {
    display: 'block',
  },
  block: {
    fill: theme.palette.common.black,
    stroke: theme.palette.common.white,
    opacity: 1,
  },
  blockText: {
    fontSize: '12px',
    color: theme.palette.text.primary,
  },
  event: { fill: theme.palette.color.orange[200], stroke: theme.palette.color.orange[200] },
  inActive: { fill: theme.palette.common.white },
  onHold: { fill: theme.palette.color.deepOrange[700] },
  afterCallWork: { fill: theme.palette.color.purple[900] },
  active: { fill: theme.palette.color.lightBlue[800] },
  unapproved: { fill: theme.palette.color.red[700] },
  approved: { fill: theme.palette.color.grey[400] }, // Will be blue (theme.palette.color.lightBlue[800]) again once approved and undefined is differentiated
})

class DesktopTape extends Component {
  static propTypes = {
    /** Provided by `react-i18next.translate` decorator */
    t: PropTypes.func.isRequired,
    /** Provided by `beam/dist/styles.withSytles` decorator */
    classes: PropTypes.object.isRequired,
    /** event data for the given call */
    events: PropTypes.array,
    /** Interaction states for given call */
    interactionStates: PropTypes.array,
    /** Application usage for given call */
    appUse: PropTypes.array,
    /** what time the call started */
    startTime: PropTypes.number,
    /** Length of the call */
    callDuration: PropTypes.number,
    /** Callback for time updates */
    setTime: PropTypes.func,
    zoomed: PropTypes.bool,
    showEvents: PropTypes.bool,
    heightUnitsPerMs: PropTypes.number.isRequired,
    graphHeight: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    theme: PropTypes.object,
  }

  static defaultProps = {
    events: [],
    interactionStates: [],
    appUse: [],
    startTime: 0,
    callDuration: 0,
    setTime: () => {},
    zoomed: true,
    showEvents: true,
  }

  shouldComponentUpdate(nextProps, nextState) {
    const propsChanged = !isEmpty(difference(nextProps, this.props))
    const stateChanged = !isEmpty(difference(nextState, this.state))
    return propsChanged || stateChanged
  }

  getBarClass = type => {
    const { classes } = this.props
    switch (type) {
      case 'EVENT':
        return classes.event
      case 'Inactive':
        return classes.inActive
      case 'OnHold':
        return classes.onHold
      case 'AfterCallWork':
        return classes.afterCallWork
      case 'Active':
      default:
        return classes.active
    }
  }

  getTypeLabel = type => {
    const { t } = this.props
    switch (type) {
      case 'OnHold':
        return t('onHold')
      case 'Active':
        return t('active')
      case 'Inactive':
        return t('inactive')
      case 'AfterCallWork':
        return t('afterCallWork')
      default:
        return type
    }
  }

  getColumnWidth = () => this.props.theme.spacing(3)
  getCenteringOffset = () =>
    this.props.zoomed ? 0 : this.props.width / 2 - this.getColumnWidth() - this.props.theme.spacing(0.5)
  getBarStartTime = start => start - this.props.startTime
  getBarPosition = start => this.getBarStartTime(start) * this.props.heightUnitsPerMs
  getBarHeight = (start, duration) =>
    Math.min(duration, this.props.callDuration - this.getBarStartTime(start)) * this.props.heightUnitsPerMs
  getTextPosition = (start, duration) =>
    this.getBarPosition(start) + (x => (x === 0 ? 0 : x / 2))(this.getBarHeight(start, duration)) + 6

  render() {
    const {
      theme,
      t,
      classes,
      startTime,
      events,
      interactionStates,
      appUse,
      setTime,
      zoomed,
      showEvents,
      graphHeight,
      width,
    } = this.props

    const {
      getBarClass,
      getTypeLabel,
      getColumnWidth,
      getCenteringOffset,
      getBarStartTime,
      getBarPosition,
      getBarHeight,
      getTextPosition,
    } = this

    return (
      <svg width={width} height={graphHeight} className={classes.svg}>
        {interactionStates.map(({ start, duration, type }, i) => (
          <Tooltip
            key={'i' + i}
            disableFocusListener
            title={(() => (
              <div>
                <div>
                  {t('type')}: <strong>{getTypeLabel(type)}</strong>
                </div>
                <div>
                  {t('start')}: <strong>{msToHHMMSS(start - startTime)}</strong>
                </div>
                <div>
                  {t('duration')}: <strong>{msToHHMMSS(duration)}</strong>
                </div>
              </div>
            ))()}
          >
            <g onClick={() => setTime(getBarStartTime(start))} style={{ cursor: 'pointer' }}>
              <Rect
                x={getCenteringOffset()}
                y={getBarPosition(start)}
                rx={theme.shape.borderRadius}
                ry={theme.shape.borderRadius}
                width={getColumnWidth()}
                height={getBarHeight(start, duration)}
                className={classNames(classes.block, getBarClass(type))}
              />
            </g>
          </Tooltip>
        ))}
        {showEvents &&
          // Events usage is currently undefined
          events.map(({ start, duration, source, sourcetype, type }, i) => (
            <Tooltip
              key={'e' + i}
              disableFocusListener
              title={(() => (
                <div>
                  <div>
                    <strong>{t('event')}</strong>
                  </div>
                  <div>
                    {t('source')}: <strong>{source}</strong>
                  </div>
                  <div>
                    {t('type')}: <strong>{sourcetype}</strong>
                  </div>
                  <div>
                    {t('start')}: <strong>{msToHHMMSS(start - startTime)}</strong>
                  </div>
                  <div>
                    {t('duration')}: <strong>{msToHHMMSS(duration)}</strong>
                  </div>
                </div>
              ))()}
            >
              <g onClick={() => setTime(getBarStartTime(start))} style={{ cursor: 'pointer' }}>
                <Rect
                  x={getCenteringOffset() + 1}
                  y={getBarPosition(start)}
                  width={getColumnWidth() - 2}
                  height={getBarHeight(start, duration)}
                  className={classNames(classes.block, getBarClass(type))}
                />
              </g>
            </Tooltip>
          ))}
        {appUse.map(({ name, hasDocument, documentLabel, start, duration, notApproved }, i) => (
          <Tooltip
            key={'a' + i}
            disableFocusListener
            title={(() => (
              <div>
                <div>
                  <strong>{name}</strong>
                </div>
                {hasDocument && (
                  <div>
                    {t('document')}:&nbsp;
                    <strong>
                      {documentLabel.map((labelPart, n) =>
                        n === 0 ? ( //Stays on the same line as the label
                          <span key={n}>{labelPart}</span>
                        ) : (
                          <div key={n}>{labelPart}</div>
                        ),
                      )}
                    </strong>
                  </div>
                )}
                <div>
                  {t('start')}: <strong>{msToHHMMSS(start - startTime)}</strong>
                </div>
                <div>
                  {t('duration')}: <strong>{msToHHMMSS(duration)}</strong>
                </div>
                <div>
                  {t('status')}: <strong>{notApproved ? t('notApproved') : t('approved')}</strong>
                </div>
              </div>
            ))()}
          >
            <g onClick={() => setTime(getBarStartTime(start))} style={{ cursor: 'pointer' }}>
              <Rect
                x={getCenteringOffset() + getColumnWidth() + theme.spacing(1)}
                y={getBarPosition(start)}
                rx={theme.shape.borderRadius}
                ry={theme.shape.borderRadius}
                width={getColumnWidth()}
                height={getBarHeight(start, duration)}
                className={classNames(classes.block, notApproved ? classes.unapproved : classes.approved)}
              />
              {zoomed && (
                <Text
                  x={getCenteringOffset() + getColumnWidth() * 2 + theme.spacing(2)}
                  y={getTextPosition(start, duration)}
                  className={classNames(classes.blockText)}
                >
                  {name}
                </Text>
              )}
            </g>
          </Tooltip>
        ))}
      </svg>
    )
  }
}

export { DesktopTape as UnstyledDesktopTape }
export default withTranslation()(withStyles(styles, { withTheme: true })(DesktopTape))
